/**
 * Solar metrics for pre-contract product cards.
 * Mirrors the Dashboard V2 System & Design card: the saved RetrieveSystemDesign
 * snapshot wins, and the live oneDRAW layout is offered as a hint when it differs.
 */
import {
    getSolarMetricsSyncDetail,
    parseDesignDataRecord,
    resolveSolarAnnualProductionKwh,
    systemSizeKwFromSnapshot,
} from '../../helpers/systemDesignSolarMetrics';
import { preContractSystemSizeKw } from './preContractPricing';
import { estimateLayoutAnnualKwh } from './preContractModuleLayout';
import {
    averageIncludedMultiplier,
    type PreContractPpwMetricsOverride,
} from './preContractPpwPricing';
import type { PreContractIntakeForm } from './preContractTypes';
import type { ActiveLossesVendor } from '../../Constants';

export type PreContractSolarMetricsSource = 'ai-design' | 'saved-design' | 'live-layout';

export type PreContractSolarMetrics = {
    systemKw: number | null;
    annualProductionKwh: number | null;
    multiplier: number | null;
    source: PreContractSolarMetricsSource;
    liveSystemKw: number | null;
    liveAnnualProductionKwh: number | null;
    liveMultiplier: number | null;
};

function positiveNumber(value: unknown): number | null {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
}

export function resolvePreContractSolarMetrics(input: {
    form: PreContractIntakeForm;
    systemDesign: unknown;
    metricsOverride?: PreContractPpwMetricsOverride | null;
}): PreContractSolarMetrics {
    const { form, systemDesign, metricsOverride } = input;

    const liveSystemKw = positiveNumber(preContractSystemSizeKw(form));
    const liveProductionKwh = positiveNumber(
        estimateLayoutAnnualKwh({
            layouts: form.roofModuleLayouts,
            roofSelections: form.roofSelections,
            moduleWattage: form.moduleTypeWattage,
        }),
    );
    const liveMultiplier = positiveNumber(
        averageIncludedMultiplier(form.roofModuleLayouts, form.roofSelections),
    );

    const overrideSystemKw = positiveNumber(metricsOverride?.systemKw);
    const overrideProductionKwh = positiveNumber(metricsOverride?.annualProductionKwh);
    const overrideMultiplier = positiveNumber(metricsOverride?.multiplier);
    if (overrideSystemKw != null || overrideProductionKwh != null || overrideMultiplier != null) {
        return {
            systemKw: overrideSystemKw ?? liveSystemKw,
            annualProductionKwh: overrideProductionKwh ?? liveProductionKwh,
            multiplier: overrideMultiplier ?? liveMultiplier,
            source: 'ai-design',
            liveSystemKw,
            liveAnnualProductionKwh: liveProductionKwh,
            liveMultiplier,
        };
    }

    const sd =
        systemDesign && typeof systemDesign === 'object'
            ? (systemDesign as Record<string, unknown>)
            : null;

    if (sd) {
        const designData = parseDesignDataRecord(sd);
        const savedSystemKw = positiveNumber(systemSizeKwFromSnapshot(sd, designData));
        const savedMultiplier = positiveNumber(sd.Multiplier_Unrounded ?? sd.Multiplier);
        const savedProductionKwh = positiveNumber(
            resolveSolarAnnualProductionKwh({
                sd,
                designData,
                systemKw: savedSystemKw,
                multiplier: savedMultiplier,
                snapshotOnly: true,
            }),
        );

        if (savedSystemKw != null || savedProductionKwh != null) {
            return {
                systemKw: savedSystemKw ?? liveSystemKw,
                annualProductionKwh: savedProductionKwh ?? liveProductionKwh,
                multiplier: savedMultiplier ?? liveMultiplier,
                source: 'saved-design',
                liveSystemKw,
                liveAnnualProductionKwh: liveProductionKwh,
                liveMultiplier,
            };
        }
    }

    return {
        systemKw: liveSystemKw,
        annualProductionKwh: liveProductionKwh,
        multiplier: liveMultiplier,
        source: 'live-layout',
        liveSystemKw,
        liveAnnualProductionKwh: liveProductionKwh,
        liveMultiplier,
    };
}

export type PreContractSolarLiveHints = {
    systemKw: string | null;
    multiplier: string | null;
};

const NO_HINTS: PreContractSolarLiveHints = { systemKw: null, multiplier: null };

export function preContractSolarLiveHints(
    metrics: PreContractSolarMetrics,
    liveModuleCount: number,
): PreContractSolarLiveHints {
    if (metrics.source !== 'saved-design') return NO_HINTS;

    const sync = getSolarMetricsSyncDetail({
        finalizedSystemKw: metrics.systemKw,
        finalizedMultiplier: metrics.multiplier,
        liveSystemKw: metrics.liveSystemKw,
        liveMultiplier: metrics.liveMultiplier,
        liveModuleCount,
    });

    return {
        systemKw:
            sync.systemKwOutOfSync && sync.liveSystemKw != null
                ? `oneDRAW: ${sync.liveSystemKw.toFixed(2)} kW`
                : null,
        multiplier:
            sync.multiplierOutOfSync && sync.liveMultiplier != null
                ? `oneDRAW: ${Number(sync.liveMultiplier.toFixed(3))}`
                : null,
    };
}

function roundedEqual(
    left: number | null | undefined,
    right: number | null | undefined,
    digits: number,
): boolean {
    if (left == null && right == null) return true;
    if (left == null || right == null) return false;
    const factor = 10 ** digits;
    return Math.round(left * factor) === Math.round(right * factor);
}

function formatKw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return `${value.toFixed(2)} kW`;
}

function formatKwh(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return `${Math.round(value).toLocaleString()} kWh`;
}

function formatMultiplier(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return String(Number(value.toFixed(3)));
}

function lossesLabel(vendor: ActiveLossesVendor | null | undefined): string {
    if (!vendor) return '—';
    return vendor === 'EnFin' ? 'EnFin' : 'oneDRAW';
}

export type SolarOneDrawDesignChange = {
    id: 'systemSize' | 'production' | 'multiplier' | 'losses';
    label: string;
    from: string;
    to: string;
};

export type SolarOneDrawDesignDetailsInput = {
    savedSystemKw: number | null;
    savedProductionKwh: number | null;
    savedMultiplier: number | null;
    savedLossesVendor: ActiveLossesVendor | null;
    liveSystemKw: number | null;
    liveProductionKwh: number | null;
    displayedProductionKwh: number | null;
    displayedMultiplier: number | null;
    liveLossesVendor: ActiveLossesVendor | null;
};

/**
 * Finalize is required only when the Solar card (or a selected max-layout)
 * differs from the last finalized design. Unselected All Designs previews are ignored.
 */
export function listSolarOneDrawDesignDetailChanges(
    input: SolarOneDrawDesignDetailsInput,
): SolarOneDrawDesignChange[] {
    const hasSaved =
        input.savedSystemKw != null ||
        input.savedProductionKwh != null ||
        input.savedMultiplier != null ||
        input.savedLossesVendor != null;
    if (!hasSaved) return [];

    const changes: SolarOneDrawDesignChange[] = [];
    const liveSize = input.liveSystemKw ?? input.savedSystemKw;
    const displayedProduction =
        input.displayedProductionKwh ?? input.savedProductionKwh;
    const displayedMultiplier = input.displayedMultiplier ?? input.savedMultiplier;

    if (!roundedEqual(liveSize, input.savedSystemKw, 2)) {
        changes.push({
            id: 'systemSize',
            label: 'System size',
            from: formatKw(input.savedSystemKw),
            to: formatKw(liveSize),
        });
    }
    if (!roundedEqual(displayedProduction, input.savedProductionKwh, 0)) {
        changes.push({
            id: 'production',
            label: 'Production',
            from: formatKwh(input.savedProductionKwh),
            to: formatKwh(displayedProduction),
        });
    }
    if (!roundedEqual(displayedMultiplier, input.savedMultiplier, 3)) {
        changes.push({
            id: 'multiplier',
            label: 'Multiplier',
            from: formatMultiplier(input.savedMultiplier),
            to: formatMultiplier(displayedMultiplier),
        });
    }
    if (
        input.savedLossesVendor &&
        input.liveLossesVendor &&
        input.savedLossesVendor !== input.liveLossesVendor
    ) {
        changes.push({
            id: 'losses',
            label: 'Losses source',
            from: lossesLabel(input.savedLossesVendor),
            to: lossesLabel(input.liveLossesVendor),
        });
    }
    return changes;
}

export function solarOneDrawDesignDetailsChanged(
    input: SolarOneDrawDesignDetailsInput,
): boolean {
    return listSolarOneDrawDesignDetailChanges(input).length > 0;
}
